import React from "react";
import {
  Table,
  Select,
  InputNumber,
  Input,
  Switch,
  Button,
  Tooltip,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { computeLine, money } from "../constants";
import UnitInput from "../UnitInput";

const { Option } = Select;

/**
 * Editable invoice line-items grid. `items` is an array held in the parent's
 * state; `onChange` receives the new array. `catalog` is the list of saved
 * items for the dropdown. `stateRates` ({ SERVICE, PHYSICAL_GOOD } tax
 * percentages for the customer's state, or null) sets the tax on lines added
 * or switched to another type; without it a catalog item keeps its own
 * default rate.
 */
export default function LineItems({ items, onChange, catalog, currency, stateRates = null }) {
  const stateTax = (productType) => (stateRates && stateRates[productType]) || 0;
  const update = (index, patch) => {
    const next = items.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const addRow = () => {
    onChange([
      ...items,
      {
        key: Date.now() + Math.random(),
        product_id: null,
        product_type: "SERVICE",
        product_name: "",
        description: "",
        unit: "",
        quantity: 1,
        rate: 0,
        discount_percentage: 0,
        is_taxable: true,
        tax_percentage: stateTax("SERVICE"),
      },
    ]);
  };

  const removeRow = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const onSelectCatalog = (index, productId) => {
    const prod = catalog.find((c) => c._id === productId);
    if (prod) {
      update(index, {
        product_id: prod._id,
        product_name: prod.product_name,
        product_type: prod.product_type,
        unit: prod.unit || "",
        rate: prod.sale_price || prod.regular_price || 0,
        is_taxable: prod.is_taxable !== false,
        tax_percentage: stateRates
          ? stateTax(prod.product_type)
          : prod.default_tax_rate_percentage || 0,
        discount_percentage: prod.default_discount_rate_percentage || 0,
        description: prod.description || "",
      });
    } else {
      update(index, { product_id: productId });
    }
  };

  const columns = [
    {
      title: "Item",
      width: 260,
      render: (text, record, index) => (
        <div>
          <Select
            allowClear
            showSearch
            placeholder="Select item or type below"
            optionFilterProp="children"
            style={{ width: "100%", marginBottom: 4 }}
            value={record.product_id || undefined}
            onChange={(v) => onSelectCatalog(index, v)}
          >
            {(catalog || []).map((c) => (
              <Option key={c._id} value={c._id}>
                {c.product_name}
                {c.category && c.category.name ? ` (${c.category.name})` : ""}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Item name"
            value={record.product_name}
            onChange={(e) => update(index, { product_name: e.target.value })}
            style={{ marginBottom: 4 }}
          />
          <Input.TextArea
            placeholder="Description"
            autoSize={{ minRows: 1 }}
            value={record.description}
            onChange={(e) => update(index, { description: e.target.value })}
          />
          <Select
            size="small"
            value={record.product_type}
            onChange={(v) =>
              update(
                index,
                stateRates ? { product_type: v, tax_percentage: stateTax(v) } : { product_type: v }
              )
            }
            style={{ width: 140, marginTop: 4 }}
          >
            <Option value="SERVICE">Service</Option>
            <Option value="PHYSICAL_GOOD">Physical Good</Option>
          </Select>
        </div>
      ),
    },
    {
      title: "Qty / Unit",
      width: 150,
      render: (text, record, index) => (
        <div>
          <InputNumber
            min={0}
            value={record.quantity}
            onChange={(v) => update(index, { quantity: v })}
            style={{ width: "100%" }}
          />
          <UnitInput
            size="small"
            placeholder="Unit"
            value={record.unit}
            onChange={(v) => update(index, { unit: v })}
            style={{ marginTop: 2, width: "100%" }}
          />
        </div>
      ),
    },
    {
      title: "Rate",
      width: 110,
      render: (text, record, index) => (
        <InputNumber
          min={0}
          step={0.01}
          prefix="$"
          value={record.rate}
          onChange={(v) => update(index, { rate: v })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Disc %",
      width: 90,
      render: (text, record, index) => (
        <InputNumber
          min={0}
          max={100}
          value={record.discount_percentage}
          onChange={(v) => update(index, { discount_percentage: v })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Tax",
      width: 120,
      render: (text, record, index) => (
        <div>
          <Tooltip title='Toggle off for "Not Tax"'>
            <Switch
              size="small"
              checked={record.is_taxable !== false}
              onChange={(checked) => update(index, { is_taxable: checked })}
            />
          </Tooltip>
          <InputNumber
            min={0}
            disabled={record.is_taxable === false}
            value={record.is_taxable === false ? 0 : record.tax_percentage}
            onChange={(v) => update(index, { tax_percentage: v })}
            style={{ width: "100%", marginTop: 4 }}
            formatter={(v) => `${v}%`}
            parser={(v) => (v || "").replace("%", "")}
          />
        </div>
      ),
    },
    {
      title: "Total",
      width: 110,
      align: "right",
      render: (text, record) => {
        const c = computeLine(record);
        return <strong>{money(c.amount_total, currency)}</strong>;
      },
    },
    {
      title: "",
      width: 40,
      render: (text, record, index) => (
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeRow(index)}
        />
      ),
    },
  ];

  return (
    <div>
      <Table
        size="small"
        dataSource={items}
        columns={columns}
        pagination={false}
        rowKey={(r, i) => r.key || r._id || i}
      />
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addRow}
        style={{ marginTop: 8 }}
        block
      >
        Add Line Item
      </Button>
    </div>
  );
}
